/* eslint-disable import/no-named-as-default-member */
import { call, put } from 'redux-saga/effects'
import _ from 'lodash'
import geolib from 'geolib'
import OrderActions from '../reducers/hasuraOrderReducer'
import LocationActions from '../reducers/locationReportReducer'
import firebase from '../config/firebase'
import Hasura from '../config/hasura'
import districts from '../config/district'
import utils from '../utils'

export function* fetchOrders({ merchant_key, startFrom, endAt, extraFields = [] }) {
  const condition = merchant_key
    ? `{merchant_key: {_eq: ${merchant_key}}, created_at: {_gte: "${startFrom}", _lte: "${endAt}"}}`
    : `{created_at: {_gte: "${startFrom}", _lte: "${endAt}"}}`
  const fields = extraFields.join(',')
  try {
    const query = `
      query{
        orders(where: ${condition} ) {
          firebase_key
          customer_tel
          customer_key
          status
          merchant_commission_rate
          promotion_discount
          promotion_cost_sharing
          voucher_discount_amount: voucher_actual_discount_amount
          subtotal

          merchant_key
          merchant_name
          branch_key
          branch_name

          created_at
          updated_at
          ${fields}
        }
      }
    `

    const { orders } = yield call(Hasura.execute, query)

    yield put(OrderActions.fetchOrdersSuccess(orders))
  } catch (err) {
    yield put(OrderActions.fetchOrdersError(err))
  }
}

export function* getSaleReports({ startFrom, endAt }) {
  yield put(OrderActions.setSaleReportLoading(true))

  const variables = { start_from: Number(startFrom), end_at: Number(endAt) }
  try {
    const query = `
      query($start_from: numeric!, $end_at: numeric!){
        orders(where: { created_at: {_gte: $start_from, _lte: $end_at}} ) {
          firebase_key
          customer_tel
          customer_key
          status
          merchant_commission_rate
          promotion_discount
          promotion_cost_sharing
          voucher_discount_amount: voucher_actual_discount_amount
          subtotal

          merchant_key
          merchant_name
          branch_key
          branch_name

          created_at
          updated_at
          deliverer_key
          merchant_key
          merchant_name
          merchant_type
          merchant_vat_rate
          merchant_commission_rate
          merchant_markup_percentage
          merchant_branch_pay_later
          merchant_branch_pay_later_type

          branch_name

          delivery_fee
          delivery_commission_rate
          delivery_discount
          promotion_cost_sharing
          rejected_code
          payments {tran_id, provider}

          voucher_type
          voucher_code
          voucher_discount_amount: voucher_actual_discount_amount

          customer_name
          customer_tel

          status
          region
          subtotal
          accepted_by
          dynamic_fees
        }
      }
    `

    const { orders } = yield call(Hasura.execute, query, variables)

    yield put(OrderActions.setSaleReports(orders))
  } catch (err) {
    yield put(OrderActions.setSaleReports(err))
  }
}

export function* getRefundOrders({ startFrom, endAt, refunded = false }) {
  yield put(OrderActions.setRefundOrderLoading(true))

  try {
    const query = `
      query {
        orders(
          where: {
            created_at: { _gte: ${startFrom}, _lte: ${endAt} },
            payments: {},
            refunded: { _eq: ${refunded} },
            refund_to: {_eq: "online_payment" },
            status: { _in: ["rejected", "delivered"] }
          }
          order_by: { created_at: asc }
        ) {
          id
          firebase_key
          payments { amount provider }
          createdAt: created_at
          customerName: customer_name
          customerTel: customer_tel
          status
          refundTo: refund_to
          refunded
          refundedAt: refunded_at
          subtotal
          deliveryFee: delivery_fee
          merchantVatRate: merchant_vat_rate
          dynamicFees: dynamic_fees
          voucherDiscountAmount: voucher_discount_amount
          promotionCostSharing: promotion_cost_sharing
          promotionDiscount: promotion_discount
          deliveryDiscount: delivery_discount
        }
      }
    `

    const { orders } = yield call(Hasura.execute, query)

    const ordersData = orders.reduce((result, order) => {
      // eslint-disable-next-line no-param-reassign
      result[order.firebase_key] = order
      return result
    }, {})

    yield put(OrderActions.setRefundOrders(ordersData))
  } catch (err) {
    yield put(OrderActions.setRefundOrderLoading(false))
  }
}

export function* getMerchantCategories() {
  try {
    const merchantRef = firebase.database().ref('restaurants')
    const merchantSnapshot = yield call([merchantRef, merchantRef.once], 'value')

    const tagRef = firebase.database().ref('tags')
    const tagSnapshot = yield call([tagRef, tagRef.once], 'value')

    const menuRef = firebase.database().ref('menus')
    const menuSnapshot = yield call([menuRef, menuRef.once], 'value')

    const data = arrangeMerchantCategories(merchantSnapshot.val(), menuSnapshot.val(), tagSnapshot.val())

    yield put(OrderActions.setMerchantCategories(data))
  } catch (error) {
    yield put(OrderActions.setMerchantCategoryError(error))
  }
}

export function* getPromoCodeOrders({ startFrom, endAt }) {
  yield put(OrderActions.setPromoCodeOrderLoading(true))
  const variables = {
    startFrom,
    endAt,
  }
  try {
    const query = `
      query($startFrom: numeric, $endAt: numeric) {
        orders(where:
          {
            _or: [
              { voucher_code: {_neq: ""} },
              { promotion_discount: { _gt: 0 } },
              { delivery_discount: { _is_null: false, _gt: 0 } }
            ],
            created_at: {_gte: $startFrom, _lte: $endAt},
            status: {_eq: "delivered"}
          },
          order_by: { created_at: asc }) {
          delivery_discount
          subtotal
          created_at
          voucher_code
          voucher_type
          voucher_discount_type
          voucher_discount_amount: voucher_actual_discount_amount
          firebase_key
          region
          customer_name
          merchant_name
          promotion_discount
          promotion_cost_sharing
          customer_stats {
            order_count
          }
          voucher
        }
      }
    `

    const { orders } = yield call(Hasura.execute, query, variables)
    yield put(OrderActions.setPromoCodeOrders(orders))
  } catch (err) {
    yield put(OrderActions.setPromoCodeOrderLoading(false))
  }
}

export function* getPromotionExpense({ startFrom, endAt }) {
  yield put(OrderActions.setPromotionExpenseLoading(true))

  try {
    const query = `
      query {
        orders(where: { created_at: {_gte: "${startFrom}", _lte: "${endAt}"}, status: { _eq: "delivered" } promotion_cost_sharing: { _is_null: false } } order_by: { created_at: asc }) {
          subtotal
          branchId: branch_key
          branchName: branch_name
          merchantId: merchant_key
          merchantName: merchant_name
          costSharing: promotion_cost_sharing
          commissionRate: merchant_commission_rate
          promotionDiscount: promotion_discount
        }
      }
    `

    const { orders } = yield call(Hasura.execute, query)
    const promotionExpense = utils.order.serializePromotionExpense(orders)

    yield put(OrderActions.setPromotionExpense(promotionExpense))
  } catch (err) {
    yield put(OrderActions.setPromotionExpenseLoading(false))
  }
}

export function* getLocationReport({ startFrom, endAt }) {
  yield put(LocationActions.setOrderLocationsLoading(true))

  try {
    const query = `
      query {
        orders(where: { created_at: {_gte: "${startFrom}", _lte: "${endAt}"} }) {
          firebase_key
          created_at
          merchant_name
          customer_location
          merchant_location
        }
      }
    `

    const { orders } = yield call(Hasura.execute, query)
    const districtData = _.map(districts, ({ name, coordinates }) => ({
      polygon: coordinates.map(z => ({
        longitude: z[0],
        latitude: z[1],
      })),
      name,
    }))

    const filteredOrders = orders.map(order => {
      const {
        customer_location: {
          coordinates: [cus_lng, cus_lat],
        },
        merchant_location: {
          coordinates: [mer_lng, mer_lat],
        },
        firebase_key,
        merchant_name,
        branch_name,
        created_at,
      } = order

      const customerCoord = {
        latitude: cus_lat,
        longitude: cus_lng,
      }
      const merchantCoord = {
        latitude: mer_lat,
        longitude: mer_lng,
      }

      let customerDistrict = 'other'
      let merchantDistrict = 'other'

      _.each(districtData, ({ polygon, name }) => {
        if (geolib.isPointInside(customerCoord, polygon)) customerDistrict = name
        if (geolib.isPointInside(merchantCoord, polygon)) merchantDistrict = name
      })

      return {
        merchant_name,
        branch_name,
        firebase_key,
        created_at,
        customerDistrict,
        merchantDistrict,
      }
    })
    yield put(LocationActions.setOrderLocations(filteredOrders))
  } catch (err) {
    yield put(LocationActions.setOrderLocationsLoading(false))
  }
}

function arrangeMerchantCategories(merchants, menus, tags) {
  if (!merchants && !menus && !tags) return {}
  const categories = _.reduce(
    menus,
    (accum, menu) => {
      accum = {
        ...accum,
        ..._.reduce(
          menu.categories,
          (acc, cat, key) => {
            acc[key] = cat.name
            return acc
          },
          {}
        ),
      }
      return accum
    },
    {}
  )

  const merchantCategoriesAndTags = _.reduce(
    merchants,
    (accum, merchant) => {
      const { region, categories: merchCats, tags: merchTags, key: merchKey } = merchant

      if (merchCats) {
        accum[merchKey] = {
          categories: _.map(merchCats[region], (cat, key) => categories[key]),
          tags: _.map(merchTags, (tag, key) => tags[key]),
        }
      }
      return accum
    },
    {}
  )

  return merchantCategoriesAndTags
}



// WEBPACK FOOTER //
// ./src/sagas/hasuraOrderSaga.js